import ComplementHeader from '@/components/Movements/WorkOrders/Details/Complements/ComplementHeader';
import WorkOrderComplementTabs from '@/components/Movements/WorkOrders/Details/Complements/WorkOrderComplementTabs';
import WorkOrderFooter from '@/components/Movements/WorkOrders/Details/WorkOrderFooter';
import WorkOrderInvoiceComplements, {
  WorkOrderInvoiceFormValues,
  invoiceValuesModelsOptions,
} from '@/components/Movements/WorkOrders/Details/WorkOrderInvoice/WorkOrderInvoiceComplements';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import { PageProps } from '@/types/PageProps';
import workOrderValidationTabs from '@/utils/functions/workOrderValidationTabs';
import { Skeleton, Divider, Row, Col, Empty, Card } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import './style.less';
import { useCallback, useEffect, useState } from 'react';
import { useWorkOrderComplementInvoiceValues } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { Group } from '@/components/_common';
import WorkOrderInvoiceValues from '@/components/Movements/WorkOrders/Details/WorkOrderInvoice/WorkOrderInvoiceValues';
import {
  InvoiceValuesItem,
  InvoiceValuesItemType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import {
  useInvoiceCreateNFc,
  useInvoiceCreateNFe,
  useInvoiceCreateNFs,
  useInvoiceList,
} from '@/core/domain/invoice/invoices/invoices.hooks';
import {
  InvoiceDocumentType,
  InvoiceItemType,
  InvoiceOriginReferenceEntity,
  InvoiceRecipientType,
  InvoiceTaxpayerType,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useWorkorderForms } from '@/components/Movements/WorkOrders/Details/WorkOrderFormProvider';
import { getWorkorderComplementNumber } from '@/utils/functions/getWorkorderComplementNumber';
import {
  InvoiceItem,
  InvoiceItemTaxes,
  InvoiceOriginCategory,
  InvoiceOriginReference,
} from '@/core/domain/invoice/invoices/invoices.types';
import { useTaxCouponCreate } from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  useCustomerGeneral,
  useCustomerReducedQuery,
} from '@/core/domain/people/Customers/Customers.hooks';
import {
  CouponOriginEntity,
  CouponOriginType,
  TaxCouponItemType,
} from '@/core/domain/coupon/coupons/coupons.types';
import { PersonType } from '@/core/domain/people/Customers/Customers.types';

export enum WorkorderInvoiceValuesType {
  particular = 'particular',
  insurer = 'insurer',
  franchise = 'franchise',
  porto = 'porto',
}

export const workorderCouponOriginTypes = {
  [WorkorderInvoiceValuesType.franchise]: CouponOriginType.INSURANCE_DEDUCTIBLE,
  [WorkorderInvoiceValuesType.insurer]: CouponOriginType.INSURANCE,
  [WorkorderInvoiceValuesType.particular]: CouponOriginType.PRIVATE,
  [WorkorderInvoiceValuesType.porto]: CouponOriginType.PRIVATE,
};

const WorkOrderInvoicePage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [workorderInvoiceFormValues, setWorkorderData] =
    useState<WorkOrderInvoiceFormValues>();

  const { data: invoiceValuesData, ...invoiceValues } =
    useWorkOrderComplementInvoiceValues();

  const isEmptyInvoiceValues =
    !invoiceValuesData?.franchise &&
    !invoiceValuesData?.insurer &&
    !invoiceValuesData?.particular &&
    !invoiceValuesData?.porto;

  const {
    workorder: { data: workorder },
    complement: { data: complement, complementIdentifier },
    company: {
      invoiceSettingsByCompany: { data: invoiceSettingsByCompany },
    },
  } = useWorkorderDetailContext();

  const groupInvoiceItemsNfsBySubArea =
    invoiceSettingsByCompany?.configNfs?.groupInvoiceItemsNfsBySubArea;

  const customerDetail = useCustomerReducedQuery(workorder?.customer?.id);

  const { data: customerData } = useCustomerGeneral(
    Number(workorder?.customer?.id),
  );

  const { hasFormChange } = useWorkorderForms();

  const invoiceCreateNFs = useInvoiceCreateNFs();
  const invoiceCreateNFc = useInvoiceCreateNFc();
  const invoiceCreateNFe = useInvoiceCreateNFe();
  const taxCouponCreate = useTaxCouponCreate();

  const invoiceListData = useInvoiceList({
    entity: InvoiceOriginReferenceEntity.WORK_ORDER,
    entityId: workorderInvoiceFormValues?.complements
      .filter((item) => item.checked)
      .map((item) => item.id),
  });

  const invoiceItems = flatInfiniteData(invoiceListData.data);

  useEffect(() => {
    if (workorderInvoiceFormValues && workorder?.id) {
      getInvoiceValues(workorderInvoiceFormValues);
    }
  }, [workorderInvoiceFormValues]);

  async function getInvoiceValues(data: WorkOrderInvoiceFormValues) {
    if (data && workorder?.id) {
      await invoiceValues.mutateAsync({
        workorderId: workorder?.id,
        complementsIds: data.complements
          .filter((item) => item.checked)
          .map((item) => item.id),
        model: data.invoiceModelType,
      });
    }
  }

  const getInvoiceCategory = (
    invoiceValuesType: WorkorderInvoiceValuesType,
  ) => {
    const types: Record<WorkorderInvoiceValuesType, InvoiceOriginCategory> = {
      particular: InvoiceOriginCategory.PRIVATE,
      insurer: InvoiceOriginCategory.INSURANCE,
      franchise: InvoiceOriginCategory.PRIVATE,
      porto: InvoiceOriginCategory.SUPPLIER,
    };

    return types[invoiceValuesType];
  };

  async function invoiceGenerate(
    data: InvoiceValuesItem[],
    recipientType: InvoiceRecipientType,
    invoiceValuesType: WorkorderInvoiceValuesType,
  ) {
    const invoiceType = invoiceValuesModelsOptions.find(
      (item) => item.value === workorderInvoiceFormValues?.invoiceModelType,
    )?.invoiceType;

    if (workorderInvoiceFormValues && workorder && invoiceType) {
      const complementsChecked = workorderInvoiceFormValues.complements.filter(
        (complement) => !!complement.checked,
      );

      await Promise.all(
        data.map(async (dataItem) => {
          const originReference = dataItem.items.reduce((acc, item) => {
            if (!item.workOrderComplementId) {
              return [
                ...acc,
                ...complementsChecked
                  .filter(
                    (complement) =>
                      !acc.some((a) => a.entityId === complement.id),
                  )
                  .map((complement) => ({
                    entity: InvoiceOriginReferenceEntity.WORK_ORDER,
                    entityCode: getWorkorderComplementNumber(
                      workorder.number,
                      complement.number,
                    ),
                    entityId: complement.id,
                    workOrderId: workorder?.id,
                    invoiceCategory: getInvoiceCategory(invoiceValuesType),
                  })),
              ];
            } else if (
              !acc.some((i) => i.entityId === item.workOrderComplementId)
            ) {
              return [
                ...acc,
                {
                  entity: InvoiceOriginReferenceEntity.WORK_ORDER,
                  entityCode: getWorkorderComplementNumber(
                    workorder.number,
                    item.workOrderComplementNumber,
                  ),
                  entityId: item.workOrderComplementId,
                  workOrderId: workorder?.id,
                  invoiceCategory: getInvoiceCategory(invoiceValuesType),
                },
              ];
            }
            return acc;
          }, [] as InvoiceOriginReference[]);

          const obj = {
            companyId: workorderInvoiceFormValues.companyId,
            originReference,
            recipientType,
            customerId:
              recipientType === InvoiceRecipientType.CUSTOMER
                ? workorder?.customer?.id
                : undefined,
            insurerId:
              recipientType === InvoiceRecipientType.INSURER
                ? complement?.insurer?.id
                : undefined,
            supplierId:
              recipientType === InvoiceRecipientType.SUPPLIER
                ? dataItem.client.id
                : undefined,
          };

          const invoiceItems = dataItem.items.map<InvoiceItem>(
            ({
              valueDiscount,
              description,
              subAreaName,
              isInternal,
              ...item
            }) => ({
              quantity: item.quantity,
              type:
                item.type === 'Service'
                  ? InvoiceItemType.TASK
                  : InvoiceItemType.PART,
              taskId: item.type === 'Service' ? item.id : undefined,
              partId: item.type === 'Part' ? item.id : undefined,
              valueUnitary: item.unitPrice,
              valueDiscount,
              description,
              subAreaName: groupInvoiceItemsNfsBySubArea
                ? subAreaName
                : undefined,
              isInternal: groupInvoiceItemsNfsBySubArea
                ? isInternal
                : undefined,
            }),
          );

          if (!!dataItem && dataItem.model === 59 && workorder.customer) {
            return await taxCouponCreate.mutateAsync({
              companyId: obj.companyId,
              recipient: {
                clientName: workorder.customer.name,
                id: workorder.customer.id,
                cpfCnpj: customerDetail.data?.cpfCnpj || '',
              },
              origins: originReference.map((reference) => ({
                ...reference,
                entityId: String(reference.entityId),
                entity: reference.entity as unknown as CouponOriginEntity,
                entityType: workorderCouponOriginTypes[invoiceValuesType],
              })),
              couponItems:
                invoiceItems.map((item) => ({
                  description: String(item.description),
                  itemId: (item.type === InvoiceItemType.PART
                    ? item.partId
                    : item.taskId) as number,
                  quantity: item.quantity,
                  type:
                    item.type === InvoiceItemType.TASK
                      ? TaxCouponItemType.TASK
                      : TaxCouponItemType.PART,
                  unitValue: item.valueUnitary,
                  discount: item.valueDiscount || undefined,
                  unitMeasure: item.unitMeasurement,
                })) || [],
            });
          } else if (
            !!dataItem &&
            dataItem.type === InvoiceValuesItemType.Service
          ) {
            return await invoiceCreateNFs.mutateAsync({
              ...obj,
              invoiceType: InvoiceType.NFS,
              invoiceItem: invoiceItems.map((service) => {
                const { valueDiscount, ...restValues } = service;

                return {
                  ...restValues,

                  unconditionalDiscount:
                    typeof valueDiscount === 'number' ? valueDiscount : null,

                  itemTaxes: {
                    withheld: workorder?.customer?.withheld,
                  } as InvoiceItemTaxes,
                };
              }),
              isIrpjCsllRetention: customerData?.retentionIRPJorCSLL,
            });
          } else if (!!dataItem && dataItem.model === 65) {
            return await invoiceCreateNFc.mutateAsync({
              ...obj,
              finalConsumer: dataItem.client.personType === PersonType.Natural,
              invoiceItem: invoiceItems,
              invoiceType,
              documentType: InvoiceDocumentType.EXIT,
              isIrpjCsllRetention: customerData?.retentionIRPJorCSLL,
            });
          } else if (!!dataItem && dataItem.model === 55) {
            return await invoiceCreateNFe.mutateAsync({
              ...obj,
              finalConsumer: dataItem.client?.taxpayerType
                ? dataItem.client?.taxpayerType ===
                  InvoiceTaxpayerType.NON_CONTRIBUTOR
                : dataItem.client.personType === PersonType.Natural,
              invoiceItem: invoiceItems,
              invoiceType,
              documentType: InvoiceDocumentType.EXIT,
              isIrpjCsllRetention: customerData?.retentionIRPJorCSLL,
              taxpayerType: customerData?.taxpayerType,
            });
          }
        }),
      );
    }
  }

  const getLoading = useCallback(
    (recipientType: InvoiceRecipientType) => {
      return (
        (invoiceCreateNFs.isLoading &&
          invoiceCreateNFs.variables?.recipientType === recipientType) ||
        (invoiceCreateNFc.isLoading &&
          invoiceCreateNFc.variables?.recipientType === recipientType) ||
        (invoiceCreateNFe.isLoading &&
          invoiceCreateNFe.variables?.recipientType === recipientType)
      );
    },
    [
      invoiceCreateNFs.isLoading,
      invoiceCreateNFc.isLoading,
      invoiceCreateNFe.isLoading,
    ],
  );

  return (
    <>
      <Helmet
        title={`${
          complementIdentifier ? `${complementIdentifier} | ` : ''
        }${intl.formatMessage({
          id: route.name,
        })}`}
      />

      <WorkOrderComplementTabs
        permissions={route.permissions}
        route={workOrderValidationTabs(
          route,
          complement?.type,
          complement?.status,
        )}
        isLoading={false}
      >
        <ComplementHeader />

        <Row className="invoce-cards" gutter={[24, 24]}>
          <Col span={24} md={12}>
            <WorkOrderInvoiceComplements
              onSubmit={setWorkorderData}
              onChange={() => {
                if (!!invoiceValuesData) {
                  setWorkorderData(undefined);
                  invoiceValues.reset();
                }
              }}
            />
          </Col>

          <Col span={24} md={12}>
            <Skeleton loading={invoiceValues.isLoading}>
              {!invoiceValuesData && (
                <Group
                  position="center"
                  align="center"
                  style={{ height: '100%' }}
                >
                  <Empty
                    description={`Não há dados para gerar nota fiscal, verifique os complementos.`}
                  />
                </Group>
              )}

              {!!invoiceValuesData && (
                <>
                  <Row gutter={[24, 24]}>
                    {isEmptyInvoiceValues && (
                      <Col span={24}>
                        <Card style={{ borderRadius: 10 }}>
                          <Empty description="Não há dados financeiros para os complementos selecionados" />
                        </Card>
                      </Col>
                    )}

                    {!!invoiceValuesData.franchise && (
                      <WorkOrderInvoiceValues
                        modelType={workorderInvoiceFormValues?.invoiceModelType}
                        complements={workorderInvoiceFormValues?.complements}
                        companyId={workorderInvoiceFormValues?.companyId}
                        invoiceItems={invoiceItems}
                        title="Franquia"
                        invoiceValues={invoiceValuesData.franchise}
                        onSubmit={(data) =>
                          invoiceGenerate(
                            data,
                            InvoiceRecipientType.CUSTOMER,
                            WorkorderInvoiceValuesType.franchise,
                          )
                        }
                        loading={getLoading(InvoiceRecipientType.CUSTOMER)}
                        invoiceValuesType={WorkorderInvoiceValuesType.franchise}
                      />
                    )}

                    {!!invoiceValuesData.insurer && (
                      <WorkOrderInvoiceValues
                        modelType={workorderInvoiceFormValues?.invoiceModelType}
                        complements={workorderInvoiceFormValues?.complements}
                        invoiceItems={invoiceItems}
                        companyId={workorderInvoiceFormValues?.companyId}
                        title="Seguradora"
                        invoiceValues={invoiceValuesData.insurer}
                        onSubmit={(data) =>
                          invoiceGenerate(
                            data,
                            InvoiceRecipientType.INSURER,
                            WorkorderInvoiceValuesType.insurer,
                          )
                        }
                        loading={getLoading(InvoiceRecipientType.INSURER)}
                        invoiceValuesType={WorkorderInvoiceValuesType.insurer}
                      />
                    )}

                    {!!invoiceValuesData.particular && (
                      <WorkOrderInvoiceValues
                        modelType={workorderInvoiceFormValues?.invoiceModelType}
                        complements={workorderInvoiceFormValues?.complements}
                        companyId={workorderInvoiceFormValues?.companyId}
                        invoiceItems={invoiceItems}
                        title="Particular"
                        invoiceValues={invoiceValuesData.particular}
                        onSubmit={(data) =>
                          invoiceGenerate(
                            data,
                            InvoiceRecipientType.CUSTOMER,
                            WorkorderInvoiceValuesType.particular,
                          )
                        }
                        loading={getLoading(InvoiceRecipientType.CUSTOMER)}
                        invoiceValuesType={
                          WorkorderInvoiceValuesType.particular
                        }
                      />
                    )}

                    {!!invoiceValuesData.porto && (
                      <WorkOrderInvoiceValues
                        modelType={workorderInvoiceFormValues?.invoiceModelType}
                        complements={workorderInvoiceFormValues?.complements}
                        companyId={workorderInvoiceFormValues?.companyId}
                        invoiceItems={invoiceItems}
                        title="Fornecedor (Porto)"
                        invoiceValues={invoiceValuesData.porto}
                        onSubmit={(data) =>
                          invoiceGenerate(
                            data,
                            InvoiceRecipientType.SUPPLIER,
                            WorkorderInvoiceValuesType.porto,
                          )
                        }
                        loading={getLoading(InvoiceRecipientType.SUPPLIER)}
                        invoiceValuesType={WorkorderInvoiceValuesType.porto}
                      />
                    )}
                  </Row>
                </>
              )}
            </Skeleton>
          </Col>
        </Row>

        <Divider />

        <WorkOrderFooter disabled={!hasFormChange} />
      </WorkOrderComplementTabs>
    </>
  );
};

export default WorkOrderInvoicePage;
